import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { kitLabel } from "@/lib/kit-label";
import { Kit } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faFileExport,
  faPen,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";
import { toast } from "sonner";
import { EditFieldDialog } from "./EditFieldDialog";

type HeyApiResult<T = unknown> =
  | { data: T; error: undefined; response: Response }
  | { data: undefined; error: unknown; response: Response };

type KitQuickSettingsUIProps = {
  kit: Kit;
  onNameChange: (name: string) => Promise<HeyApiResult<unknown>>;
  onExport?: () => Promise<unknown>;
};

const throwOnApiError = <T,>(
  result: HeyApiResult<T>,
  fallback: string,
): void => {
  if (!result.error) return;
  const e = result.error as {
    userResponse?: { message?: string };
    response?: { message?: string };
  };
  const msg = e.userResponse?.message ?? e.response?.message ?? fallback;
  toast.error(msg);
  throw new Error(msg);
};

export const KitQuickSettingsUI = ({
  kit,
  onNameChange,
  onExport,
}: KitQuickSettingsUIProps) => {
  const [dialogType, setDialogType] = useState<"name" | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleNameChange = async (name: string) => {
    throwOnApiError(await onNameChange(name), "Failed to update name.");
    toast.success("Name updated!");
  };

  const handleExport = async () => {
    if (!onExport) return;
    setIsExporting(true);
    try {
      await onExport();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={(e) => e.stopPropagation()}
            aria-label="More Actions"
            title="More Actions"
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-40">
          <DropdownMenuItem
            className="gap-0 truncate"
            onClick={() => setDialogType("name")}
          >
            <FontAwesomeIcon icon={faPen} className="mr-2" />
            <span>Edit Name</span>
          </DropdownMenuItem>
          {onExport && (
            <DropdownMenuItem
              className="gap-0 truncate"
              onClick={handleExport}
              disabled={isExporting}
            >
              <FontAwesomeIcon icon={faFileExport} className="mr-2" />
              <span>
                {isExporting ? "Exporting..." : `Export ${kitLabel(kit)}`}
              </span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <EditFieldDialog
        open={dialogType === "name"}
        onOpenChange={(open) => !open && setDialogType(null)}
        field="name"
        currentValue={kit.name}
        onSave={handleNameChange}
      />
    </>
  );
};
